import { ulid } from "ulid";

const API_BASE = import.meta.env.VITE_API_BASE ?? "https://api.vereinfach.app";

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  status: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  constructor(status: number, data: unknown, message: string) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    let data: unknown = null;
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
    throw new ApiError(res.status, data, `${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// In-flight mutations keyed by (method + path + body). A double-click — or any
// concurrent duplicate — shares one promise, so exactly one HTTP request with
// one request_id reaches the server. The server's request_log dedup then
// guarantees the operation runs once. Sequential (genuinely distinct) calls
// each get their own id, so two deliberate identical payments are never merged.
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Mutating call. Pass an explicit `requestId` to keep idempotency stable across
 * a manual retry of one logical action; otherwise a fresh id is minted and
 * concurrent duplicates are collapsed via the in-flight map above.
 */
export function mutate<T>(
  path: string,
  body: Record<string, unknown> = {},
  method: "POST" | "PATCH" | "DELETE" = "POST",
  requestId?: string,
): Promise<T> {
  const key = `${method} ${path} ${JSON.stringify(body)}`;
  const existing = inFlight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = api<T>(path, {
    method,
    body: JSON.stringify({ request_id: requestId ?? ulid(), ...body }),
  }).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

/** German user-facing messages for engine error codes. */
const ERROR_MESSAGES: Record<string, string> = {
  stale_preview: "Die Daten haben sich geändert. Bitte die Aktion erneut öffnen und prüfen.",
  request_conflict:
    "Diese Anfrage wurde bereits mit anderen Angaben verwendet. Bitte neu versuchen.",
  confirmation_required: "Bitte die Änderung bestätigen.",
  period_already_invoiced: "Für diese Periode existiert bereits eine Rechnung.",
  number_taken: "Diese Rechnungsnummer ist bereits vergeben.",
  coverage_overlap: "Dieser Zeitraum ist bereits verrechnet.",
  duplicate_subscription: "Dieses Mitglied hat diesen Beitrag bereits.",
  reanchor_blocked_by_issued:
    "Eine ausgestellte Rechnung deckt diesen Zeitraum bereits — zuerst stornieren.",
  package_in_use:
    "Die Laufzeit kann nicht geändert werden, solange Mitglieder diesen Beitrag haben. Archiviere ihn und lege einen neuen an.",
  member_has_documents:
    "Dieses Mitglied hat Rechnungen oder Beiträge und kann nicht gelöscht werden. Nutze stattdessen «Austritt».",
  invoice_not_draft: "Diese Rechnung ist bereits freigegeben und kann nicht mehr geändert werden.",
  invoice_cancelled: "Diese Rechnung wurde storniert.",
  not_applicable_to_storno: "Auf einer Stornorechnung ist das nicht möglich.",
  payment_on_unissued: "Zahlungen sind erst nach dem Freigeben möglich.",
  recipient_incomplete: "Die Empfängeradresse ist unvollständig.",
  total_below_minimum: "Der Betrag muss mindestens CHF 0.01 sein.",
  qr_settings_invalid: "Die QR-Rechnungs-Einstellungen sind unvollständig (IBAN/Adresse prüfen).",
  covers_inactive_period:
    "Diese Rechnung deckt eine Periode eines pausierten oder beendeten Beitrags.",
  member_cap_exceeded: "Mitglieder-Limit erreicht — Plan upgraden.",
  member_has_active_subscription:
    "Dieses Mitglied hat einen laufenden Beitrag. Beende ihn zuerst, dann archivieren.",
  member_has_open_invoice:
    "Dieses Mitglied hat offene Rechnungen. Schliesse sie zuerst ab, dann archivieren.",
  member_archived: "Dieses Mitglied ist bereits archiviert.",
  plan_too_small: "Der gewählte Plan ist zu klein für die aktuelle Mitgliederzahl.",
  same_tier: "Das ist bereits der aktuelle Plan.",
  owner_only: "Nur die Besitzerin oder der Besitzer des Vereins kann das Abo verwalten.",
  no_plan: "Es ist kein kostenpflichtiges Abo aktiv.",
  vat_uid_required: "Für die MwSt wird eine gültige UID (CHE-…) benötigt.",
  vat_rate_required: "Für die MwSt wird ein Steuersatz grösser als 0 benötigt.",
  tenant_has_issued_documents: "Es existieren ausgestellte Rechnungen — Löschen nicht möglich.",
  invalid_input: "Ungültige Eingabe.",
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const data = typeof e.data === "object" && e.data ? (e.data as Record<string, unknown>) : null;
    const code = data ? (data.error as string) : "";
    // VAT validation errors arrive as invalid_input + a detail discriminator.
    if (code === "invalid_input" && typeof data?.detail === "string") {
      const detail = data.detail as string;
      if (ERROR_MESSAGES[detail]) return ERROR_MESSAGES[detail];
    }
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (code) return code;
  }
  return e instanceof Error ? e.message : "Unbekannter Fehler";
}

export type CapExceeded = {
  error: "member_cap_exceeded";
  current: number;
  cap: number;
  plan: string;
};

/** Detects the 402 member_cap_exceeded response so the UI can nudge to upgrade. */
export function capExceeded(e: unknown): CapExceeded | null {
  if (
    e instanceof ApiError &&
    e.status === 402 &&
    typeof e.data === "object" &&
    e.data &&
    (e.data as { error?: string }).error === "member_cap_exceeded"
  ) {
    return e.data as CapExceeded;
  }
  return null;
}
